import { foldForSearch } from '../fold-for-search.js';
/**
 * Nombre de suffixes numériques essayés avant d'abandonner (M6 : `dupont.jean2`,
 * `dupont.jean3`, …). Un vrai homonyme épuise ça en pratique jamais — cette borne protège
 * contre un `isTaken` qui renverrait toujours `true`, pas contre un usage normal.
 */
const MAX_NUMERIC_SUFFIX_ATTEMPTS = 1000;
/**
 * Replie les accents et retire tout ce qui n'est pas `[a-z0-9]` : sans ça « Le Gall » produit
 * un préfixe contenant l'espace, et « Éric » un login commençant par `é`.
 *
 * ⚠️ **Le point tombe ici, et c'est voulu** : c'est une brique de **nom**, pas de login entier —
 * les deux fabrications ci-dessous posent le `.` elles-mêmes, à la place qu'elles décident.
 * `normalizeLogin` est l'autre moitié, pour un login **saisi** à la main.
 */
function normalize(value) {
    return foldForSearch(value).replace(/[^a-z0-9]/g, '');
}
/**
 * Replie une saisie humaine sur la forme d'un login (M6) : accents repliés, minuscules, et rien
 * d'autre que `[a-z0-9.]` — `Éq. JL TB` donne `eq.jltb`.
 *
 * ⚠️ **Le point est gardé, contrairement à `normalize`** : il sépare les deux moitiés de toutes
 * les formes de M6 (`d.jean`, `eq.jltb`). Le retirer d'une saisie recollerait `eq.jltb` en
 * `eqjltb` — un identifiant que personne ne reconnaîtrait comme celui d'une équipe.
 *
 * ⚠️ **Une saisie se normalise, elle ne se refuse pas** (M21) : un identifiant tapé sur une
 * tablette, au clavier tactile, arrive avec une majuscule automatique et parfois un espace de
 * trop. Refuser aurait renvoyé l'humain à un format qu'il ne voit nulle part ; on replie, et
 * l'écran montre ce qui sera enregistré avant qu'il valide.
 *
 * Le résultat peut être **vide** — une saisie qui ne garde ni lettre, ni chiffre, ni **point** :
 * « … » et « --- » tombent à rien, « ... » non, le point étant gardé juste au-dessus. C'est à
 * l'appelant de refuser ce vide, `User.login` étant `NOT NULL` et `UNIQUE` (M6) ;
 * `teamLoginSchema` le fait, et ne refuse rien d'autre.
 */
export function normalizeLogin(value) {
    return foldForSearch(value).replace(/[^a-z0-9.]/g, '');
}
async function withNumericSuffix(base, isTaken) {
    for (let suffix = 2; suffix < 2 + MAX_NUMERIC_SUFFIX_ATTEMPTS; suffix++) {
        const candidate = `${base}${suffix}`;
        if (!(await isTaken(candidate)))
            return candidate;
    }
    throw new Error(`aucun login libre à partir de "${base}" après ${MAX_NUMERIC_SUFFIX_ATTEMPTS} suffixes ` +
        'numériques — `isTaken` renvoie-t-il toujours `true` ?');
}
/**
 * Fabrique le login d'un compte nominatif (M6) : initiale du nom `.` prénom, en minuscules —
 * `Jean Dupont` devient `d.jean`.
 *
 * ⚠️ Le login d'un compte **nominatif** est généré une fois, puis stocké : corriger
 * l'orthographe d'un nom ne doit pas changer l'identifiant de connexion de quelqu'un, et
 * `updateUserSchema` ne le laisse pas entrer. C'est à l'appelant de ne rappeler cette fonction
 * qu'à la création du compte. ⚠️ **Le compte partagé d'une équipe, lui, se renomme** depuis le
 * 11/09/2026 (`updateTeamSchema`, M6) : là c'est `buildTeamLogin` qui **propose**, et un humain
 * qui tranche.
 *
 * **Collision** : le préfixe s'allonge d'une lettre à la fois (`d.jean` → `du.jean`) jusqu'à
 * épuiser le nom. Toujours pris une fois le nom entier utilisé — un vrai homonyme — bascule
 * sur un suffixe numérique (`dupont.jean2`, `dupont.jean3`, …).
 *
 * @throws si le nom ou le prénom est vide une fois normalisé : `.jean` n'identifie personne
 * et entrerait en collision avec le prochain sans nom.
 */
export async function buildLogin(lastName, firstName, isTaken) {
    const lastNamePart = normalize(lastName);
    const firstNamePart = normalize(firstName);
    if (!lastNamePart || !firstNamePart) {
        throw new Error(`buildLogin : nom ("${lastName}") ou prénom ("${firstName}") vide`);
    }
    for (let prefixLength = 1; prefixLength <= lastNamePart.length; prefixLength++) {
        const candidate = `${lastNamePart.slice(0, prefixLength)}.${firstNamePart}`;
        if (!(await isTaken(candidate)))
            return candidate;
    }
    return withNumericSuffix(`${lastNamePart}.${firstNamePart}`, isTaken);
}
/**
 * Fabrique le login d'un compte d'équipe (M6) : `eq.` + les initiales des deux agents, en
 * minuscules — équipe `JL·TB` devient `eq.jltb`. Forme reprise à l'identique du prototype
 * back-office validé (`t.login = 'eq.' + (t.a + t.b).toLowerCase()`), pas inventée ici.
 *
 * ⚠️ **Ce que cette fonction rend est une proposition, pas un verdict** (11/09/2026). Elle sert
 * à la création du groupe, et à **pré-remplir** le champ de la fiche équipe quand le binôme
 * change : l'écran l'affiche, l'humain valide ou écrit le sien. Rien ne la rappelle côté serveur
 * pour « rafraîchir » un login existant — ce serait écraser une valeur choisie à la main, en
 * silence, au prochain changement de binôme (garde-fou n°3).
 *
 * **Collision** : deux équipes aux mêmes initiales produiraient le même login. M6 est muette
 * là-dessus ; plutôt que de laisser remonter la violation de `User.login UNIQUE`, on bascule
 * sur un suffixe numérique comme pour un homonyme (`eq.jltb2`). Pas de préfixe à allonger :
 * les initiales sont déjà la forme la plus courte.
 *
 * @throws si les deux initiales sont vides une fois normalisées — `eq.` seul n'identifie
 * aucune équipe.
 */
export async function buildTeamLogin(a, b, isTaken) {
    const base = teamLoginFor(a, b);
    if (base === null) {
        throw new Error(`buildTeamLogin : initiales ("${a}", "${b}") vides`);
    }
    return (await isTaken(base)) ? withNumericSuffix(base, isTaken) : base;
}
/**
 * La **forme** du login d'équipe, sans regarder ce qui est déjà pris : `eq.jltb`. C'est ce que
 * `buildTeamLogin` fabrique avant d'aller interroger la base, et ce qu'un écran peut afficher
 * comme **proposition** — lui n'a pas de base à interroger (D17), et une proposition n'a pas à
 * être unique : elle est vérifiée à l'enregistrement, où la collision devient un `409`.
 *
 * `null` plutôt qu'une exception quand les deux initiales se replient sur rien : c'est le cas
 * d'un écran qui propose devant une équipe mal formée, pas une erreur de programmation. Celui qui
 * doit refuser refuse — `buildTeamLogin` ci-dessus.
 */
export function teamLoginFor(a, b) {
    const initials = normalize(a) + normalize(b);
    return initials === '' ? null : `eq.${initials}`;
}
//# sourceMappingURL=login.js.map